import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, XCircle, Loader2, ArrowLeft, Clock } from 'lucide-react';
import { api, formatApiError } from '@/admin/api';

const PaymentSuccessPage = () => {
  const [params] = useSearchParams();
  const reference = params.get('reference') || params.get('trxref') || '';
  const [state, setState] = useState({ loading: true, error: '', data: null });

  useEffect(() => {
    if (!reference) {
      setState({ loading: false, error: 'No payment reference supplied.', data: null });
      return;
    }
    api
      .get(`/api/payments/verify/${encodeURIComponent(reference)}`)
      .then((r) => setState({ loading: false, error: '', data: r.data }))
      .catch((err) => setState({ loading: false, error: formatApiError(err), data: null }));
  }, [reference]);

  const { loading, error, data } = state;
  const ok = data && data.status === 'successful';
  const pending = data && data.status === 'pending';
  const typeLabel = data?.payment_type === 'donation' ? 'Donation' : 'Summit Registration';
  const displayName = data?.anonymous ? 'Anonymous' : data?.name;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <section className="py-20 bg-gradient-to-br from-primary/5 via-background to-accent/5 min-h-[60vh]">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="shadow-elevated" data-testid="payment-success-card">
            <CardContent className="pt-10 pb-10 text-center space-y-6">
              {loading && (
                <div className="flex flex-col items-center gap-3" data-testid="verify-loading">
                  <Loader2 className="w-12 h-12 text-primary animate-spin" />
                  <h2 className="text-2xl font-serif font-semibold">Verifying your payment…</h2>
                  <p className="text-muted-foreground text-sm">Please don&apos;t close this page.</p>
                </div>
              )}

              {!loading && ok && (
                <>
                  <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                    <CheckCircle2 className="w-12 h-12 text-green-600" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-serif font-bold text-foreground" data-testid="success-heading">
                      Payment Successful
                    </h2>
                    <p className="text-muted-foreground mt-2">
                      Thank you{data.anonymous ? '' : `, ${data.name}`}. We&apos;ve recorded your {typeLabel.toLowerCase()}.
                    </p>
                  </div>
                  <div className="bg-muted/40 rounded-lg p-5 text-left space-y-3 max-w-md mx-auto">
                    <Row label={data.payment_type === 'donation' ? 'Donor' : 'Attendee'} value={displayName} />
                    <Row label="Purpose" value={typeLabel} />
                    <Row label="Amount Paid" value={`${data.currency} ${Number(data.amount).toLocaleString()}`} />
                    <Row label="Status" value={<span className="text-green-600 font-medium">{data.status}</span>} />
                    <Row label="Channel" value={data.channel || '—'} />
                    <Row label="Reference" value={<span className="font-mono text-xs break-all">{data.reference}</span>} />
                    {data.paid_at && <Row label="Paid at" value={new Date(data.paid_at).toLocaleString()} />}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
                    <Link to="/">
                      <Button variant="outline"><ArrowLeft className="w-4 h-4 mr-2" /> Back to Home</Button>
                    </Link>
                  </div>
                </>
              )}

              {!loading && !ok && pending && (
                <>
                  <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto">
                    <Clock className="w-12 h-12 text-amber-600" />
                  </div>
                  <h2 className="text-3xl font-serif font-bold text-foreground">Payment Pending</h2>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    Your transaction is still being processed. We will update this page automatically when confirmation arrives — you can refresh in a minute.
                  </p>
                  <p className="text-xs text-muted-foreground font-mono break-all">Ref: {data.reference}</p>
                  <Link to="/payment"><Button variant="outline">Back to Payment</Button></Link>
                </>
              )}

              {!loading && !ok && !pending && (
                <>
                  <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto">
                    <XCircle className="w-12 h-12 text-red-600" />
                  </div>
                  <h2 className="text-3xl font-serif font-bold text-foreground">Payment Not Completed</h2>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    {error || (data ? `Status: ${data.status}` : 'We could not confirm this payment.')}
                  </p>
                  {reference && (
                    <p className="text-xs text-muted-foreground font-mono break-all">Ref: {reference}</p>
                  )}
                  <Link to="/payment"><Button className="bg-primary hover:bg-primary-dark text-white">Try again</Button></Link>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </section>
      <Footer />
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex justify-between items-start gap-4 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-medium text-foreground text-right">{value}</span>
  </div>
);

export default PaymentSuccessPage;
